import { Create, getValueFromEvent, useFileUploadState, useForm, useSelect } from "@refinedev/antd";
import { FileTextOutlined, InboxOutlined, FlagOutlined } from "@ant-design/icons";
import { DatePicker, Form, Input, Select, Tag, Upload, Space } from "antd";
import { IResourceComponentsProps, useApiUrl } from "@refinedev/core";
import dayjs from "dayjs";
import { ICountry, IFlagStateCircular } from "interfaces";
import { FormSection, FormFieldGrid } from "components/form";

import React from "react";

export const FlagStateCircularCreate: React.FC<
  IResourceComponentsProps
> = () => {
  const [isFileUploaded, setIsFileUploaded] = React.useState(false);

  const {
    formProps,
    saveButtonProps,
    formLoading: isLoading,
  } = useForm<IFlagStateCircular>({ redirect: "list" });

  const { onChange, isLoading: isFileUploading } = useFileUploadState();
  const apiUrl = useApiUrl();

  function onFileUpload(e: any) {
    setIsFileUploaded(true);
    onChange(e);
  }

  const props = {
    name: "file",
    multiple: false,
    maxCount: 1,
    action: `${apiUrl}/upload/flag/circular`,
    onChange: onFileUpload,
  };

  const { selectProps: flagSelectProps } = useSelect<ICountry>({
    resource: "country",
    optionLabel: "name",
    optionValue: "name",

    pagination: {
      mode: "server"
    }
  });

  return (
    <Create
      breadcrumb={false}
      saveButtonProps={{
        ...saveButtonProps,
        disabled: isFileUploaded && !isFileUploading ? false : true,
      }}
      headerProps={{
        onBack: () => window.history.back(),
        title: "Digital Bookshelf",
        subTitle: "flag state circulars",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
      }}
    >
      <Form
        {...formProps}
        layout="vertical"
        initialValues={{
          documentType: "Circular",
        }}
      >
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
          <FormSection
            title="Circular Details"
            subtitle="Flag state circular information"
            icon={<FlagOutlined />}
            variant="elevated"
          >
            <FormFieldGrid columns={2}>
              <Form.Item
                label="Title"
                name={["title"]}
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input name="title" />
              </Form.Item>
              <Form.Item
                label="Flag"
                name={["flag"]}
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Select
                  style={{ minWidth: 200 }}
                  {...flagSelectProps}
                />
              </Form.Item>
              <Form.Item
                label="Item Number"
                name={["itemNumber"]}
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input name="itemNumber" />
              </Form.Item>
              <Form.Item
                label="Category"
                name={["category"]}
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input name="category" />
              </Form.Item>
              <Form.Item
                label="Circular Number"
                name={["circularNumber"]}
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input name="circularNumber" />
              </Form.Item>
              <Form.Item
                label="Issue Date"
                name={["issueDate"]}
                rules={[
                  {
                    required: true,
                  },
                ]}
                getValueProps={(value) => ({
                  value: value ? dayjs(value) : "",
                })}
              >
                <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                label="Document Type"
                name={["documentType"]}
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Select
                  defaultValue="circular"
                  options={[
                    { value: "circular", label: "Circular" },
                    { value: "index", label: "Index" },
                  ]}
                />
              </Form.Item>
            </FormFieldGrid>
          </FormSection>
          <FormSection
            title="File Upload"
            subtitle="Upload flag state circular file"
            icon={<FileTextOutlined />}
            variant="elevated"
          >
            <Form.Item
              label="File"
              name="file"
              valuePropName="file"
              getValueFromEvent={getValueFromEvent}
            >
              <Upload.Dragger {...props} listType="picture">
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag & drop file to this area to upload
                </p>
                <p className="ant-upload-hint">
                  Support for a single upload of flag state circular for vessel
                </p>
              </Upload.Dragger>
            </Form.Item>
          </FormSection>
        </Space>
      </Form>
    </Create>
  );
};
